import {
  Button,
  InputField,
  Switch,
  Tag,
  Label,
  Divider,
  buttonVariants,
  cn,
} from "@jem-hr/jem-ui";
import { InheritedField } from "./InheritedField";
import { GeofenceMapPreview } from "./GeofenceMapPreview";

export interface RosterShiftType {
  id: string;
  /** Shift type label, e.g. "Day", "Night", "Morning". */
  type: string;
  /** Time window, e.g. "06:00–14:00". */
  window: string;
  /** Required headcount for this shift. Empty string = fallback to assigned. */
  requiredHeadcount: string;
  assignedHeadcount: number;
}

export interface RosterConfig {
  id: string;
  name: string;
  shiftWindow: string;
  assignedHeadcount: number;
  /** Required headcount — Roster-only, NO inheritance chip (ENG-4306). */
  requiredHeadcount: string;
  /**
   * Optional per-shift-type breakdown. When set with 2+ entries, the UI renders
   * a per-shift table and top-level requiredHeadcount/assignedHeadcount act as
   * derived sums. When undefined or length === 1, the UI uses the single-field
   * pattern.
   */
  shiftTypes?: RosterShiftType[];
  /** Per-field inheritance state. */
  lateArrivalSource: "site" | "override";
  lateArrivalOverrideValue?: string;
  /** Per-Roster geofence switch (default true = use Site geofence). */
  useSiteGeofence: boolean;
  geofenceAddress?: string;
  geofenceLatitude?: string;
  geofenceLongitude?: string;
  geofenceRadius?: string;
  dirty: boolean;
}

export interface SiteDefaults {
  siteName: string;
  address: string;
  latitude: string;
  longitude: string;
  geofenceRadius: string;
  lateArrivalThreshold: string;
}

export interface RosterConfigSectionProps {
  roster: RosterConfig;
  siteDefaults: SiteDefaults;
  onChange: (rosterId: string, patch: Partial<RosterConfig>) => void;
  onRevertField: (rosterId: string, field: "lateArrival") => void;
  onToggleGeofence: (rosterId: string, useSite: boolean) => void;
  onSave: (rosterId: string) => void;
  onDiscard: (rosterId: string) => void;
  className?: string;
}

/**
 * One Roster's full configuration row, rendered inside the Accordion.
 *
 * Sections:
 *   1. Header summary (name, shift window, badge counts)
 *   2. Required headcount + assigned/clocked-in counts (Roster-only)
 *   3. Late arrival threshold (inheritable)
 *   4. Geofence: Switch + (when off) override fields + map preview
 *   5. Per-Roster Save / Discard bar (only visible when dirty)
 */
export function RosterConfigSection({
  roster,
  siteDefaults,
  onChange,
  onRevertField,
  onToggleGeofence,
  onSave,
  onDiscard,
  className,
}: RosterConfigSectionProps) {
  const effectiveAddress = roster.useSiteGeofence
    ? siteDefaults.address
    : roster.geofenceAddress ?? siteDefaults.address;
  const effectiveLat = roster.useSiteGeofence
    ? siteDefaults.latitude
    : roster.geofenceLatitude ?? siteDefaults.latitude;
  const effectiveLng = roster.useSiteGeofence
    ? siteDefaults.longitude
    : roster.geofenceLongitude ?? siteDefaults.longitude;
  const effectiveRadius = roster.useSiteGeofence
    ? siteDefaults.geofenceRadius
    : roster.geofenceRadius ?? siteDefaults.geofenceRadius;

  const hasMultipleShifts =
    roster.shiftTypes !== undefined && roster.shiftTypes.length > 1;

  return (
    <div className={cn("space-y-md py-sm", className)}>
      {/* ---------- Headcount block ---------- */}
      <div>
        <Label>
          <span className="text-sm font-semibold text-greyscale-text-title">
            Required headcount
          </span>
        </Label>

        {hasMultipleShifts ? (
          <div className="mt-sm space-y-sm">
            <p className="text-xs text-greyscale-text-caption">
              This Roster runs more than one shift type. Set the target headcount per shift, or leave blank to fall back to assigned.
            </p>
            <div className="space-y-xs">
              {roster.shiftTypes!.map((shift) => (
                <div
                  key={shift.id}
                  className="flex flex-wrap items-center gap-md"
                >
                  <div className="min-w-[180px]">
                    <p className="text-sm font-semibold text-greyscale-text-title">
                      {shift.type}
                    </p>
                    <p className="text-xs text-greyscale-text-caption">
                      {shift.window} · {shift.assignedHeadcount} assigned
                    </p>
                  </div>
                  <InputField
                    type="number"
                    min={0}
                    value={shift.requiredHeadcount}
                    placeholder="e.g. 40"
                    className="w-[160px]"
                    onChange={(e) => {
                      if (!roster.shiftTypes) return;
                      const newShifts = roster.shiftTypes.map((s) =>
                        s.id === shift.id
                          ? { ...s, requiredHeadcount: e.target.value }
                          : s
                      );
                      onChange(roster.id, {
                        shiftTypes: newShifts,
                        dirty: true,
                      });
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-xxs sm:max-w-[50%]">
            <InputField
              type="number"
              min={0}
              value={roster.requiredHeadcount}
              onChange={(e) =>
                onChange(roster.id, {
                  requiredHeadcount: e.target.value,
                  dirty: true,
                })
              }
              placeholder="e.g. 40"
            />
          </div>
        )}
      </div>

      <Divider />

      {/* ---------- Geofence: Switch + override fields + map preview ---------- */}
      <div className="space-y-sm">
        <div className="flex items-center justify-between gap-md">
          <div>
            <Label className="flex items-center gap-xs">
              <span className="text-sm font-semibold text-greyscale-text-title">
                Geofence
              </span>
              {!roster.useSiteGeofence && (
                <Tag variant="processing" size="sm">
                  Custom
                </Tag>
              )}
            </Label>
            <p className="mt-xxs text-xs text-greyscale-text-caption">
              {roster.useSiteGeofence
                ? `Same as ${siteDefaults.siteName}. Clock events validate against the Site's geofence.`
                : `This Roster has its own geofence. Changes to ${siteDefaults.siteName} won't affect it.`}
            </p>
          </div>
          <div className="flex items-center gap-xs">
            <span className="text-sm text-greyscale-text-body">
              Use custom geofence
            </span>
            <Switch
              checked={!roster.useSiteGeofence}
              onCheckedChange={(checked) =>
                onToggleGeofence(roster.id, !checked)
              }
              aria-label="Use custom geofence"
            />
          </div>
        </div>

        {!roster.useSiteGeofence && (
          <div className="grid gap-md md:grid-cols-2">
            <div className="space-y-md">
              <InputField
                label="Address"
                value={effectiveAddress}
                onChange={(e) =>
                  onChange(roster.id, {
                    geofenceAddress: e.target.value,
                    dirty: true,
                  })
                }
              />
              <div className="grid gap-md sm:grid-cols-2">
                <InputField
                  label="Latitude"
                  value={effectiveLat}
                  onChange={(e) =>
                    onChange(roster.id, {
                      geofenceLatitude: e.target.value,
                      dirty: true,
                    })
                  }
                />
                <InputField
                  label="Longitude"
                  value={effectiveLng}
                  onChange={(e) =>
                    onChange(roster.id, {
                      geofenceLongitude: e.target.value,
                      dirty: true,
                    })
                  }
                />
              </div>
              <InputField
                label="Radius (m)"
                type="number"
                value={effectiveRadius}
                onChange={(e) =>
                  onChange(roster.id, {
                    geofenceRadius: e.target.value,
                    dirty: true,
                  })
                }
              />
            </div>
            <GeofenceMapPreview
              latitude={Number(effectiveLat) || 0}
              longitude={Number(effectiveLng) || 0}
              radiusMeters={Number(effectiveRadius) || 100}
              source="override"
              siteName={siteDefaults.siteName}
            />
          </div>
        )}
      </div>

      <Divider />

      {/* ---------- Late arrival threshold (inheritable) ---------- */}
      <InheritedField
        label="Late arrival threshold (minutes)"
        source={roster.lateArrivalSource}
        siteName={siteDefaults.siteName}
        siteDefaultLabel={`${siteDefaults.lateArrivalThreshold} minutes`}
        onRevert={() => onRevertField(roster.id, "lateArrival")}
      >
        <div className="flex items-center gap-xs sm:w-1/2">
          {roster.lateArrivalSource === "site" ? (
            <>
              <InputField
                type="number"
                value={siteDefaults.lateArrivalThreshold}
                disabled
                className="flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  onChange(roster.id, {
                    lateArrivalSource: "override",
                    lateArrivalOverrideValue: siteDefaults.lateArrivalThreshold,
                    dirty: true,
                  })
                }
              >
                Override
              </Button>
            </>
          ) : (
            <InputField
              type="number"
              value={roster.lateArrivalOverrideValue ?? ""}
              onChange={(e) =>
                onChange(roster.id, {
                  lateArrivalOverrideValue: e.target.value,
                  dirty: true,
                })
              }
              className="flex-1"
            />
          )}
        </div>
      </InheritedField>

      <Divider />

      {/* ---------- Per-Roster Save / Discard ---------- */}
      <div className="flex items-center justify-end gap-xs">
        {roster.dirty && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDiscard(roster.id)}
          >
            Discard
          </Button>
        )}
        <Button
          variant="default"
          size="sm"
          disabled={!roster.dirty}
          onClick={() => onSave(roster.id)}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
}

/**
 * Header summary content for an Accordion trigger.
 * Renders the at-a-glance Roster identity + override badge so admins can spot
 * drift without expanding every row.
 */
export interface RosterAccordionSummaryProps {
  roster: RosterConfig;
  siteDefaults: SiteDefaults;
  onMoveRoster?: (rosterId: string) => void;
}

export function RosterAccordionSummary({
  roster,
  onMoveRoster,
}: RosterAccordionSummaryProps) {
  const hasShifts = roster.shiftTypes && roster.shiftTypes.length > 0;
  const totalAssigned = hasShifts
    ? roster.shiftTypes!.reduce((acc, s) => acc + s.assignedHeadcount, 0)
    : roster.assignedHeadcount;
  const shiftLabel = hasShifts
    ? roster.shiftTypes!.map((s) => s.type).join(" · ")
    : roster.shiftWindow;

  return (
    <div className="flex flex-1 flex-wrap items-center justify-between gap-sm pr-sm">
      <div className="flex items-center gap-sm">
        <div className="text-left">
          <p className="font-semibold text-greyscale-text-title">
            {roster.name}
          </p>
          <p className="text-xs text-greyscale-text-caption">
            {shiftLabel} · {totalAssigned} assigned
          </p>
        </div>
        {onMoveRoster && (
          <span
            role="button"
            tabIndex={0}
            aria-label={`Move ${roster.name} to another Site`}
            className={buttonVariants({ variant: "outline", size: "sm" })}
            onClick={(e) => {
              e.stopPropagation();
              onMoveRoster(roster.id);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onMoveRoster(roster.id);
              }
            }}
          >
            Move Roster
          </span>
        )}
      </div>
      {roster.dirty && (
        <Tag variant="pending" size="sm">
          Unsaved
        </Tag>
      )}
    </div>
  );
}
